/**
 * eventsCloud.js — Firestore layer for Event Pre-Orders.
 *
 * Two collections (see firestore.rules):
 *   events/{eventId}   — short custom-id doc; public `get`, staff `list`/write.
 *   eventOrders/{id}   — public `create` (like feedback), staff read/update.
 *
 * Mirrors the real-time onSnapshot style of expenseCloud.js.
 */
import {
  collection, doc, setDoc, getDoc, updateDoc, deleteDoc,
  addDoc, onSnapshot, query, where, orderBy, serverTimestamp, arrayUnion,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { genId, actorFrom, historyEntry, statusAction, phoneClaimKey, orderEditEntry, normalizePhone } from './eventUtils';

const EVENTS = 'events';
const ORDERS = 'eventOrders';
const MENUS = 'eventMenus';
const CLAIMS = 'eventOrderClaims';

// ── Events ───────────────────────────────────────────────────────────────────

/** Live list of every event, newest first (admin dashboard). */
export function subscribeEvents(onData, onError) {
  const q = query(collection(db, EVENTS), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError || (() => {}),
  );
}

/** Live single event — the public form uses this so open/close flips instantly. */
export function subscribeEvent(eventId, onData, onError) {
  return onSnapshot(
    doc(db, EVENTS, eventId),
    (snap) => onData(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onError || (() => {}),
  );
}

/** One-shot event read (builder edit load). */
export async function getEvent(eventId) {
  const snap = await getDoc(doc(db, EVENTS, eventId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Create an event with a short custom doc id; returns the id (the link code). */
export async function createEvent(data, user) {
  let id = genId(8);
  try {
    const existing = await getDoc(doc(db, EVENTS, id));
    if (existing.exists()) id = genId(8); // vanishingly unlikely; one retry
  } catch {
    /* offline / permissions — proceed with generated id */
  }
  const actor = actorFrom(user);
  // eslint-disable-next-line no-unused-vars
  const { history, ...rest } = data; // start a fresh audit trail
  await setDoc(doc(db, EVENTS, id), {
    ...rest,
    createdBy: actor.uid || null,
    createdByName: actor.name,
    history: [historyEntry('created', actor)],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return id;
}

/** Save builder edits. Preserves the audit trail and appends an "edited" entry. */
export async function updateEvent(eventId, data, user) {
  // eslint-disable-next-line no-unused-vars
  const { history, ...rest } = data; // never overwrite the immutable trail
  const patch = { ...rest, updatedAt: serverTimestamp() };
  if (user) patch.history = arrayUnion(historyEntry('edited', actorFrom(user)));
  await updateDoc(doc(db, EVENTS, eventId), patch);
}

/** Change status and log it to the audit timeline. */
export async function setEventStatus(eventId, status, user) {
  await updateDoc(doc(db, EVENTS, eventId), {
    status,
    updatedAt: serverTimestamp(),
    history: arrayUnion(historyEntry(statusAction(status), actorFrom(user))),
  });
}

export async function deleteEvent(eventId) {
  await deleteDoc(doc(db, EVENTS, eventId));
}

// ── Menu library (reusable, editable menus) ──────────────────────────────────

/** Live list of saved menus, most-recently-updated first. */
export function subscribeMenus(onData, onError) {
  const q = query(collection(db, MENUS), orderBy('updatedAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError || (() => {}),
  );
}

export async function createMenu(data, user) {
  const actor = actorFrom(user);
  return addDoc(collection(db, MENUS), {
    ...data,
    createdBy: actor.uid || null,
    createdByName: actor.name,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateMenu(menuId, data) {
  await updateDoc(doc(db, MENUS, menuId), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteMenu(menuId) {
  await deleteDoc(doc(db, MENUS, menuId));
}

// ── Orders ───────────────────────────────────────────────────────────────────

/** Public submit of a pre-order. Server stamps the time. */
export async function submitOrder(order) {
  return addDoc(collection(db, ORDERS), { ...order, submittedAt: serverTimestamp() });
}

/** One-shot order read by id (public `get` — used after a claim resolves). */
export async function getOrder(orderId) {
  const snap = await getDoc(doc(db, ORDERS, orderId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Cross-device "find my order": resolve a customer's order from their phone via the
 * public claim pointer (`eventOrderClaims/{eventId_phoneHash}` → orderId), then read
 * the order by id. Returns the order (with id) or null. Nothing is enumerable — you
 * must know the exact phone to derive the claim id.
 */
export async function findOrderByPhone(eventId, phone) {
  const key = phoneClaimKey(eventId, phone);
  if (!key) return null;
  const claim = await getDoc(doc(db, CLAIMS, key));
  const orderId = claim.exists() ? claim.data().orderId : null;
  return orderId ? getOrder(orderId) : null;
}

/**
 * Place OR update a pre-order, enforcing ONE order per phone per event.
 *
 * Resolution order for the target order: an explicit `knownOrderId` (an edit the
 * caller already loaded), else the phone's existing claim (auto-dedupe). When
 * updating, the original order's `deviceId` is preserved so the device-capability
 * update rule passes even from a different device. Also (re)writes the phone claim
 * so the order stays findable everywhere. Returns { orderId, updated }.
 */
export async function placeOrder(eventId, phone, payload, opts = {}) {
  const { knownOrderId = null, keepDeviceId = null, editEntry = null } = opts;
  const key = phoneClaimKey(eventId, phone);
  const claimRef = key ? doc(db, CLAIMS, key) : null;

  let orderId = knownOrderId;
  let preserveDeviceId = keepDeviceId;
  if (!orderId && claimRef) {
    const claim = await getDoc(claimRef);
    orderId = claim.exists() ? claim.data().orderId : null;
    if (orderId && preserveDeviceId == null) {
      const existing = await getOrder(orderId);
      preserveDeviceId = existing ? existing.deviceId : null;
    }
  }

  if (orderId) {
    const body = preserveDeviceId != null ? { ...payload, deviceId: preserveDeviceId } : payload;
    await updateOrder(orderId, body, editEntry || orderEditEntry(payload.respondentName || ''));
    // Claim write is best-effort: a denied/offline claim must never fail the order edit.
    if (claimRef) {
      await setDoc(claimRef, { eventId, orderId, name: payload.respondentName || '', deviceId: body.deviceId || '', at: serverTimestamp() }, { merge: true }).catch(() => {});
    }
    return { orderId, updated: true };
  }

  const ref = await submitOrder(payload);
  if (claimRef) {
    await setDoc(claimRef, { eventId, orderId: ref.id, name: payload.respondentName || '', deviceId: payload.deviceId || '', at: serverTimestamp() }).catch(() => {});
  }
  return { orderId: ref.id, updated: false };
}

/**
 * Backfill phone→order claims for orders that predate the claim system (or whose
 * claim write was denied before the rules were deployed). Safe + idempotent: only
 * writes a claim that's missing. Run from the admin Responses view (staff can list
 * orders). Best-effort per order — a failure on one never blocks the rest.
 */
export async function backfillClaims(eventId, orders) {
  let written = 0;
  for (const o of orders || []) {
    if (!o || !o.id || !normalizePhone(o.respondentPhone || '')) continue;
    const key = phoneClaimKey(eventId, o.respondentPhone);
    if (!key) continue;
    try {
      const ref = doc(db, CLAIMS, key);
      const snap = await getDoc(ref); // eslint-disable-line no-await-in-loop
      if (!snap.exists()) {
        await setDoc(ref, { eventId, orderId: o.id, name: o.respondentName || '', deviceId: o.deviceId || '', at: serverTimestamp() }); // eslint-disable-line no-await-in-loop
        written += 1;
      }
    } catch { /* best-effort — rules not deployed / offline */ }
  }
  return written;
}

/**
 * Public edit of an existing pre-order. Allowed by rules only when the incoming
 * deviceId matches the stored one (a capability the ordering device holds).
 * `editEntry` (from orderEditEntry) is appended to the order's audit timeline.
 */
export async function updateOrder(orderId, order, editEntry) {
  const patch = { ...order, updatedAt: serverTimestamp() };
  if (editEntry) patch.history = arrayUnion(editEntry);
  await updateDoc(doc(db, ORDERS, orderId), patch);
}

/** Live orders for one event (responses view). Sorted client-side (no index needed). */
export function subscribeOrders(eventId, onData, onError) {
  const q = query(collection(db, ORDERS), where('eventId', '==', eventId));
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError || (() => {}),
  );
}

/** Live full order stream — dashboard derives per-event counts from this. */
export function subscribeAllOrders(onData, onError) {
  return onSnapshot(
    collection(db, ORDERS),
    (snap) => onData(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError || (() => {}),
  );
}
